import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { DocumentListRow } from '@klear/document/trader-document'
import type { DocumentStatus } from '@klear/schema/documents'
import { Badge } from '@klear/ui/components/badge'
import { Button } from '@klear/ui/components/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@klear/ui/components/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@klear/ui/components/table'
import { cn } from '@klear/ui/lib/utils'
import { addedOn, captionOf, FileActions, typeLabel, WhereCell } from '@/app/documents/document-row'

export const PAGE_SIZES = [25, 50, 100, 250]

const REVIEW_TONE = 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'
const QUIET_TONE = 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'

const STATUS_LOOK: Record<DocumentStatus, { label: string; tone: string }> = {
  processing: { label: 'Processing', tone: REVIEW_TONE },
  needs_review: { label: 'Needs review', tone: REVIEW_TONE },
  filed: { label: 'Filed', tone: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300' },
  dismissed: { label: 'Dismissed', tone: QUIET_TONE }
}

export type TraderDocumentsTableProps = {
  rows: DocumentListRow[]
  total: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  isLoading: boolean
  traderName: (traderId: number) => string | undefined
}

export function TraderDocumentsTable({
  rows,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading,
  traderName
}: TraderDocumentsTableProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="hidden md:table-cell">Trader</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden md:table-cell">Type</TableHead>
              <TableHead>Where</TableHead>
              <TableHead className="hidden md:table-cell">Added</TableHead>
              <TableHead>
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center">
                  No documents found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((detail) => {
                const { document } = detail
                const look = STATUS_LOOK[document.status]
                const caption = captionOf(detail)
                return (
                  <TableRow key={document.id}>
                    <TableCell className="max-w-[220px] font-medium sm:max-w-[320px]">
                      <div className="truncate" title={document.name}>
                        {document.name}
                      </div>
                      {caption !== undefined && (
                        <div className="text-muted-foreground mt-0.5 truncate text-xs font-normal" title={caption}>
                          {caption}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden max-w-[160px] md:table-cell">
                      <div className="truncate">{traderName(document.traderId)}</div>
                    </TableCell>
                    <TableCell>
                      <Badge className={cn('whitespace-nowrap font-medium', look.tone)}>{look.label}</Badge>
                    </TableCell>
                    <TableCell className="hidden whitespace-nowrap md:table-cell">{typeLabel(document)}</TableCell>
                    <TableCell>
                      <WhereCell detail={detail} />
                    </TableCell>
                    <TableCell className="hidden whitespace-nowrap md:table-cell">{addedOn(document)}</TableCell>
                    <TableCell className="py-1">
                      <FileActions document={document} />
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">
            {total} document{total !== 1 ? 's' : ''}
          </span>
          <Select value={toString(pageSize)} onValueChange={(value) => onPageSizeChange(toNumber(value))}>
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map((size) => (
                <SelectItem key={size} value={toString(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-muted-foreground">per page</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-medium">
            Page {page + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(Math.max(0, page - 1))}
            disabled={page <= 0}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
